import { log, logException } from '@paradigm/logging';
import createObservable, {
  Observable,
  ReadonlyObservable,
} from '@paradigm/utils/src/createObservable';

import { subscribeWebSocket } from '#/websocket/useWsInstance';
import { Bbo } from '#/unified-rfqs/entities/drfq/bbo';
import { getRfqBbo } from '#/unified-rfqs/repositories/drfq-api';
import {
  createDrfqWebSocket,
  DrfqWebSocket,
} from '#/unified-rfqs/repositories/drfq-ws';
import { DrfqNotification } from '#/unified-rfqs/repositories/drfq-ws-types';

import { isFeatureFlagEnabled } from '../../feature-flags/utils';
import { CountMap } from './shared';

type BboObservable = ReadonlyObservable<Bbo | null>;

export interface DrfqBboService {
  /**
   * Acquire an observable to the RFQ's BBO data.
   *
   * ATTENTION: it is essential to `release()` after the observable is no
   * more in use, otherwise we won't be able to close the subscription to
   * this RFQ's bbo streaming channel. Thus, after `release()` the observable
   * could stop updating due to the end of the subscription, so make sure to
   * only release when the data is not being used anymore.
   * @param rfqId
   */
  readonly acquireRfqBbo: (
    rfqId: string,
  ) => [release: () => void, bbo: BboObservable];

  /**
   * Closes the web socket connection and clears all BBOs.
   * Discard the instance after calling this, it won't work.
   */
  readonly teardown: () => void;
}

/**
 * This instance is already provided by DrfqDependenciesProvider, (@paradigm/features/src/unified-rfqs/contexts/drfqs).
 * You can use the instance through useDrfqBbo hook (also available in the same module).
 */
export function createDrfqBboService(): DrfqBboService {
  const ws = createDrfqWebSocket();
  const bboManagerMap = new Map<string, BboManager>();
  const bboManagerRefCount = new CountMap<string>();

  ws.addNotificationHandler(handleNotification);
  ws.addEventListener('open', handleOpen);
  ws.addEventListener('close', handleDisconnect);
  ws.addEventListener('error', handleDisconnect);

  function teardown() {
    stopAllBboManagers();
    bboManagerMap.clear();
    bboManagerRefCount.clear();
    ws.removeNotificationHandler(handleNotification);
    ws.removeEventListener('open', handleOpen);
    ws.removeEventListener('close', handleDisconnect);
    ws.removeEventListener('error', handleDisconnect);
    ws.close();
  }

  function acquireRfqBbo(
    rfqId: string,
  ): [release: () => void, bbo: BboObservable] {
    const [release, bboManager] = acquireBboManager(rfqId);
    return [release, bboManager.observable];
  }

  function handleNotification(notification: DrfqNotification) {
    if (notification.topic !== 'bbo') return;
    const { rfq_id } = notification.data;
    bboManagerMap.get(rfq_id)?.notify(notification.data);
  }

  function handleOpen() {
    startAllBboManagers();
  }

  function handleDisconnect() {
    stopAllBboManagers();
  }

  function startAllBboManagers() {
    for (const bboManager of bboManagerMap.values()) {
      bboManager.start();
    }
  }

  function stopAllBboManagers() {
    for (const bboManager of bboManagerMap.values()) {
      bboManager.stop();
    }
  }

  function acquireBboManager(rfqId: string) {
    bboManagerRefCount.increment(rfqId);

    let released = false;
    const release = () => {
      // This function must be idempotent
      if (released) return;
      released = true;

      bboManagerRefCount.decrement(rfqId);
      if (bboManagerRefCount.get(rfqId) === 0) {
        bboManagerMap.get(rfqId)?.stop();
        bboManagerMap.delete(rfqId);
      }
    };

    let bboManager = bboManagerMap.get(rfqId);
    if (bboManager == null) {
      bboManager = createBboManager(ws, rfqId);
      if (ws.readyState === WebSocket.OPEN) {
        bboManager.start();
      }
      bboManagerMap.set(rfqId, bboManager);
    }

    return [release, bboManager] as const;
  }

  return { acquireRfqBbo, teardown };
}

type BboManager = ReturnType<typeof createBboManager>;

function createBboManager(ws: DrfqWebSocket, rfqId: string) {
  const rwObservable = createObservable<Bbo | null>(`drfq-bbo-${rfqId}`, null);
  const observable: BboObservable = rwObservable;

  let controller = new AbortController();
  let unsubscribeContoller = new AbortController();
  let started = false;

  function handleClose() {
    controller.abort();
    unsubscribeContoller.abort();
  }

  function stop() {
    started = false;
    controller.abort();
    if (ws.readyState === WebSocket.OPEN) {
      unsubscribeFromRfqBbo({ ws, rfqId, signal: unsubscribeContoller.signal });
    }
    ws.removeEventListener('close', handleClose);
    // If we aren't subscribed we can't keep the BBO value, as it'll
    // get out of date quickly.
    rwObservable.set(null);
  }

  /** Start syncing BBO value. */
  function start() {
    if (ws.readyState !== WebSocket.OPEN) {
      throw new Error('Disconnected web socket, cannot start');
    }

    started = true;
    controller.abort();
    controller = new AbortController();
    unsubscribeContoller.abort();
    unsubscribeContoller = new AbortController();

    ws.addEventListener('close', handleClose);

    rwObservable.set(null);
    initRfqBbo(rfqId, controller.signal, rwObservable);

    subscribeToRfqBbo({
      ws,
      signal: controller.signal,
      rfqId,
      onError: stop,
    });
  }

  function notify(bbo: Bbo) {
    if (started) {
      rwObservable.set(bbo);
    }
  }

  return { start, stop, notify, observable } as const;
}

/**
 * Starts a task that will load a BBO to the observable if it is still
 * uninitialized.
 * @param rfqId - for the BBO that will be loaded
 * @param observable - that will have value set if still null
 * @returns controller to abort the initialization task
 */
function initRfqBbo(
  rfqId: string,
  signal: AbortSignal,
  observable: Observable<Bbo | null>,
) {
  (async () => {
    const resp = await getRfqBbo({ rfqId, signal });
    if (signal.aborted) return;

    if (!resp.ok) {
      log('Failed fetching DRFQ BBO for initialization', {
        level: 'warning',
        data: { resp, rfqId },
      });
      return;
    }

    // Only initialize if bbo is still null. Being not null means we got a
    // BBO from the web socket, which will always be more up-to-date to the
    // HTTP response.
    if (observable.get() != null) return;

    observable.set(resp.data);
  })().catch((error: Error) => {
    if (error.name === 'AbortError') return;
    logException(
      {
        message: 'Unexpected failure initializing DRFQ BBO',
        data: { rfqId },
      },
      error,
    );
  });
}

interface SubscribeToRfqBboParams {
  /** web socket that will be subscribed */
  ws: DrfqWebSocket;
  /** RFQ that will get subscribed to */
  rfqId: string;
  signal: AbortSignal;
  onError: () => void;
}

/**
 * Subscribe web socket to RFQ's BBO channel
 */
function subscribeToRfqBbo(params: SubscribeToRfqBboParams) {
  const { ws, signal, rfqId, onError } = params;
  const channel = getChannelName(rfqId);

  subscribeWebSocket({
    channels: [channel],
    name: `drfq-bbo-${rfqId}`,
    signal,
    ws,
  }).catch((error: Error) => {
    if (error.name === 'AbortError') return;
    logException(
      {
        message: 'Failed subscribing to drfq bbo channel',
        data: { channel, rfqId },
      },
      error,
    );
    onError();
  });
}

interface UnsubscribeFromRfqBboParams {
  /** web socket that will be unsubscribed */
  ws: DrfqWebSocket;
  /** RFQ that will get unsubscribed from */
  rfqId: string;
  signal: AbortSignal;
}

/**
 * Unsubscribe web socket from RFQ's BBO channel
 */
function unsubscribeFromRfqBbo(params: UnsubscribeFromRfqBboParams) {
  const { ws, rfqId, signal } = params;
  const channel = getChannelName(rfqId);

  ws.request({ method: 'unsubscribe', params: { channel }, signal }).catch(
    (error: Error) => {
      if (error.name === 'AbortError') return;
      logException(
        {
          message: 'Failed unsubscribing to drfq bbo channel',
          data: { channel, rfqId },
        },
        error,
      );
    },
  );
}

function getChannelName(rfqId: string) {
  if (isFeatureFlagEnabled(`feature_um_constant_time_rfq_ticker`)) {
    return `bbo.${rfqId}.500ms`;
  }

  return `bbo.${rfqId}`;
}
